import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { api } from '../services/api'
import { TrendingUp, DollarSign, Coins, Wallet, Sparkles } from 'lucide-react'
import { Link } from 'react-router-dom'
import ReferralSection from '../components/referral/ReferralSection'
import ReferralFeeInfo from '../components/referral/ReferralFeeInfo'
import ReferralStatsByLevel from '../components/referral/ReferralStatsByLevel'
import ReferralBonuses from '../components/referral/ReferralBonuses'
import MintNFTButton from '../components/MintNFTButton'
import { QUERY_CONFIG } from '../hooks/useQueries'

export default function DashboardPage() {
  const { t } = useTranslation(['dashboard', 'common'])
  const isAuthenticated = api.isAuthenticated()
  const queryClient = useQueryClient()

  const { data: userResponse } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => api.getCurrentUser(),
    enabled: isAuthenticated,
    ...QUERY_CONFIG.currentUser,
  })

  const { data: investmentsResponse } = useQuery({
    queryKey: ['myInvestments', userResponse?.data?.id],
    queryFn: () => api.getMyInvestments(undefined, false),
    enabled: isAuthenticated && !!userResponse?.data?.id,
    ...QUERY_CONFIG.investments,
  })

  const user = userResponse?.data
  const investments = investmentsResponse?.data || []

  // Show both ACTIVE and PENDING investments (PENDING = awaiting verification)
  const activeInvestments = investments.filter(
    (inv) => inv.status === 'ACTIVE' || inv.status === 'PENDING' || inv.status === 'PENDING_VERIFICATION'
  )
  const totalPendingUSDT = investments.reduce((sum, inv) => sum + inv.pendingUSDT, 0)
  const totalPendingTAKARA = investments.reduce((sum, inv) => sum + inv.pendingTAKARA, 0)

  // v3.3: Investments ready for NFT minting
  const investmentsReadyToMint = investments.filter(
    (inv) => inv.nftReadyToMint && !inv.isNFTMinted && inv.status === 'ACTIVE'
  )

  const refetchInvestments = () => {
    queryClient.invalidateQueries({ queryKey: ['myInvestments'] })
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <Wallet className="h-16 w-16 text-gray-500 mx-auto" />
          <h2 className="text-2xl font-bold text-white">{t('dashboard:loginRequired.title')}</h2>
          <p className="text-gray-400">{t('dashboard:loginRequired.message')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">{t('dashboard:title')}</h1>
          <p className="text-gray-400">{t('dashboard:subtitle')}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Invested */}
          <div className="bg-background-card rounded-xl p-6 border border-green-900/20">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 bg-teal-500/20 rounded-lg flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-teal-400" />
              </div>
              <span className="text-sm text-gray-400">{t('dashboard:stats.totalInvested')}</span>
            </div>
            <div className="text-2xl font-bold text-white">
              ${user?.totalInvested.toLocaleString() || '0'}
            </div>
          </div>

          {/* Total Earned USDT */}
          <div className="bg-background-card rounded-xl p-6 border border-green-900/20">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 bg-gold-500/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-gold-400" />
              </div>
              <span className="text-sm text-gray-400">{t('dashboard:stats.totalEarnedUSDT')}</span>
            </div>
            <div className="text-2xl font-bold text-gold-500">
              ${user?.totalEarnedUSDT.toLocaleString() || '0'}
            </div>
          </div>

          {/* Total Mined TAKARA */}
          <div className="bg-background-card rounded-xl p-6 border border-green-900/20">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Coins className="h-5 w-5 text-green-400" />
              </div>
              <span className="text-sm text-gray-400">{t('dashboard:stats.totalMinedTAKARA')}</span>
            </div>
            <div className="text-2xl font-bold text-green-400">
              {user?.totalMinedTAKARA.toLocaleString() || '0'}
            </div>
          </div>

          {/* Active Vaults */}
          <div className="bg-background-card rounded-xl p-6 border border-green-900/20">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Wallet className="h-5 w-5 text-purple-400" />
              </div>
              <span className="text-sm text-gray-400">{t('dashboard:stats.activeVaults')}</span>
            </div>
            <div className="text-2xl font-bold text-white">{activeInvestments.length}</div>
          </div>
        </div>

        {/* NFTs Ready to Mint (v3.3) */}
        {investmentsReadyToMint.length > 0 && (
          <div className="bg-gradient-to-br from-gold-500/10 to-purple-500/10 border border-gold-500/30 rounded-xl p-6 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 bg-gold-500/20 rounded-lg flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-gold-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gold-400">{t('dashboard:nfts.title')}</h2>
                <p className="text-sm text-gray-400">
                  {t('dashboard:nfts.subtitle')}
                </p>
              </div>
            </div>
            <div className="space-y-4">
              {investmentsReadyToMint.map((inv) => (
                <div
                  key={inv.id}
                  className="bg-background-card rounded-lg p-4 border border-gold-500/20 flex flex-col md:flex-row justify-between items-start md:items-center gap-4"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`tier-${(inv.vaultTier || '').toLowerCase()} inline-block text-xs`}>
                        {inv.vaultTier}
                      </span>
                      <span className="font-medium text-white">{inv.vaultName}</span>
                    </div>
                    <div className="text-sm text-gray-400">
                      {t('dashboard:nfts.usdtAmount', { amount: inv.usdtAmount.toLocaleString() })} • {t('dashboard:nfts.apy', { apy: inv.finalAPY })}
                    </div>
                  </div>
                  <MintNFTButton
                    investmentId={inv.id}
                    vaultName={inv.vaultName}
                    usdtAmount={inv.usdtAmount}
                    mintLockedUntil={inv.mintLockedUntil}
                    onSuccess={refetchInvestments}
                    className="w-full md:w-auto"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Referral Section (v2.8) - Only shown for users who can invite */}
        <div className="mb-8">
          <ReferralSection />
        </div>

        {/* Referral Stats By Level - Shows invited users and earnings grouped by level */}
        <ReferralStatsByLevel />

        {/* Referral Bonuses - Shows bonuses given and gained */}
        <ReferralBonuses />

        {/* Referral Fee Info (v2.8) - Shown for users who came via referral */}
        <ReferralFeeInfo
          referralLevel={user?.referralLevel}
          totalPendingUSDT={totalPendingUSDT}
          totalPendingTAKARA={totalPendingTAKARA}
        />

        {/* Pending Rewards Summary - with link to Portfolio for individual claims */}
        {(totalPendingUSDT > 0 || totalPendingTAKARA > 0) && (
          <div className="bg-background-card rounded-xl p-6 border border-gold-500/30 mb-8">
            <h2 className="text-xl font-bold text-white mb-4">{t('dashboard:pendingRewards.title')}</h2>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              {totalPendingUSDT > 0 && (
                <div className="bg-background-elevated rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-1">{t('dashboard:pendingRewards.claimableUSDT')}</div>
                  <div className="text-2xl font-bold text-gold-500">
                    ${totalPendingUSDT.toFixed(2)}
                  </div>
                </div>
              )}
              {totalPendingTAKARA > 0 && (
                <div className="bg-background-elevated rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-1">{t('dashboard:pendingRewards.claimableTAKARA')}</div>
                  <div className="text-2xl font-bold text-green-400 mb-1">
                    {totalPendingTAKARA.toFixed(2)}
                  </div>
                  <div className="text-xs text-yellow-500">
                    {t('dashboard:pendingRewards.taxNote', { amount: (totalPendingTAKARA * 0.95).toFixed(2) })}
                  </div>
                </div>
              )}
            </div>
            <Link
              to="/portfolio"
              className="inline-flex items-center gap-2 text-gold-500 hover:text-gold-400 transition-colors text-sm font-medium"
            >
              {t('dashboard:pendingRewards.claimInPortfolio')}
            </Link>
          </div>
        )}

        {/* Active Investments List */}
        <div className="bg-background-card rounded-xl p-6 border border-green-900/20">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-white">{t('dashboard:yourVaults.title')}</h2>
            <Link
              to="/portfolio"
              className="text-sm text-gold-500 hover:text-gold-400 transition-colors"
            >
              {t('dashboard:yourVaults.viewAll')}
            </Link>
          </div>

          {activeInvestments.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400 mb-4">{t('dashboard:yourVaults.noInvestments')}</p>
              <Link to="/app/portfolio" className="btn-gold inline-block px-6 py-2 rounded-lg">
                {t('dashboard:yourVaults.viewPortfolio')}
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {activeInvestments.slice(0, 5).map((investment) => (
                <div
                  key={investment.id}
                  className="bg-background-elevated rounded-lg p-4 border border-green-900/20"
                >
                  <div className="flex flex-wrap justify-between items-start gap-4">
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`tier-${(investment.vaultTier || '').toLowerCase()} inline-block`}>
                          {investment.vaultTier}
                        </div>
                        {/* Status badge for non-active investments */}
                        {investment.status !== 'ACTIVE' && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            investment.status === 'PENDING' || investment.status === 'PENDING_VERIFICATION'
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : 'bg-gray-500/20 text-gray-400'
                          }`}>
                            {investment.status === 'PENDING_VERIFICATION' ? t('dashboard:yourVaults.verifying') : investment.status}
                          </span>
                        )}
                        {investment.laikaBoost && (
                          <span className="bg-purple-500/20 text-purple-400 text-xs px-2 py-0.5 rounded-full">
                            {t('dashboard:yourVaults.laikaBoost', { apy: investment.laikaBoost.additionalAPY })}
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-1">
                        {investment.vaultName}
                      </h3>
                      <p className="text-sm text-gray-400">
                        {t('dashboard:yourVaults.started')} {new Date(investment.startDate).toLocaleDateString()}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1">
                      <div>
                        <div className="text-xs text-gray-500 mb-1">{t('dashboard:yourVaults.investment')}</div>
                        <div className="text-sm font-semibold text-gold-500">
                          ${investment.usdtAmount.toLocaleString()}
                        </div>
                        {investment.laikaBoost && investment.laikaBoost.laikaAmount > 0 && (
                          <div className="text-xs text-blue-400">
                            +{investment.laikaBoost.laikaAmount.toLocaleString()} LAIKA
                          </div>
                        )}
                        {investment.takaraBoost && investment.takaraBoost.takaraAmount > 0 && (
                          <div className="text-xs text-green-400">
                            +{investment.takaraBoost.takaraAmount.toLocaleString()} TAKARA
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">{t('dashboard:yourVaults.usdtAPY')}</div>
                        <div className="text-sm font-semibold text-gold-500">
                          {investment.finalAPY}%
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">{t('dashboard:yourVaults.earnedUSDT')}</div>
                        <div className="text-sm font-semibold text-green-400">
                          ${investment.totalEarnedUSDT.toFixed(2)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">{t('dashboard:yourVaults.minedTAKARA')}</div>
                        <div className="text-sm font-semibold text-green-400">
                          {investment.totalMinedTAKARA.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
